/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:max-line-length no-magic-numbers

import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { invertHexColor } from '@gh/common-shared';
import { isNil } from 'lodash';
import { Group, Item, Path, Point, PointText, Rectangle, Size } from 'paper';
import {
  getGraphicalTakeOffBoxId,
  getGraphicalTakeOffColor,
  getGraphicalTakeOffColorPalette,
  getGraphicalTakeOffMeasurementId,
  getGraphicalTakeOffTopViewId,
  GraphicalTakeOffBox,
  GraphicalTakeOffMeasurement,
  GraphicalTakeOffTopView,
  GraphicalTakeOffViewTransform,
} from '../../resources';
import {
  composePaperEffects,
  constantScalePaperEffect,
  constantScaleStrokeWidthPaperEffect, moveOnFront, moveOnBack,
  toPaperPoint,
  visiblePaperEffect, GTO_MEASUREMENT_STROKE_WIDTH,
} from './canvas.utils';
import { GraphicalTakeOffElementBase, paperEffect, PaperElementEffect } from './graphical-take-off-element';

const dataBoxPaperEffects: PaperElementEffect[] = [
  paperEffect('visible', visiblePaperEffect),
  paperEffect('selected', (item: Item, [selected]: [boolean], [previousSelected]: [boolean]) => {
    if (selected === previousSelected) {
      return;
    }
    item.lastChild.visible = selected;
  }),
  paperEffect(['selected', 'highlighted'], (item, [selected, highlighted], [previousSelected, previousHighlighted]) => {
    if (selected === previousSelected && highlighted === previousHighlighted) {
      return;
    }

    if (highlighted) {
      moveOnFront(item, 'highlight');
    } else if (selected) {
      moveOnFront(item, 'databox');
    } else {
      moveOnBack(item, 'databox');
    }
  }),
  paperEffect(['dataBox', 'viewTransform'], (item,
                                             [dataBox, viewTransform]: [GraphicalTakeOffBox, GraphicalTakeOffViewTransform],
                                             [previousDataBox, previousViewTransform]: [GraphicalTakeOffBox, GraphicalTakeOffViewTransform]) => {
    const { point } = dataBox;
    if (isNil(previousDataBox) || dataBox.point !== previousDataBox.point || viewTransform !== previousViewTransform) {
      item.position = toPaperPoint(point);
    }
    if (isNil(previousDataBox) || dataBox.label !== previousDataBox.label) {
      (<PointText>item.children[2]).content = dataBox.label;
    }
  }),
  paperEffect('content', (item, [content]: [string], [previousContent]: [string]) => {
    if (isNil(previousContent) || content !== previousContent) {
      (<PointText>item.children[4]).content = content;
    }
  }),
  paperEffect('viewTransform', composePaperEffects(
    constantScalePaperEffect,
    (item, current, previous) => constantScaleStrokeWidthPaperEffect(item.children[0], <any>current, <any>previous))),
];

@Component({
  selector: 'gh-graphical-take-off-canvas-data-box',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GraphicalTakeOffCanvasDataBoxComponent extends GraphicalTakeOffElementBase {
  @Input() top: GraphicalTakeOffTopView;
  @Input() measurement: GraphicalTakeOffMeasurement;
  @Input() dataBox: GraphicalTakeOffBox;
  @Input() visible: boolean;
  @Input() selected: boolean;
  @Input() highlighted: boolean;
  @Input() content: string;
  @Input() viewTransform: GraphicalTakeOffViewTransform;

  constructor() {
    super(dataBoxPaperEffects);
  }

  protected createPaperItem(): Item {
    let width = 70;       // Box width
    const padding = 20;   // Text padding
    const height = 50;    // Box height
    const headScale = 3;  // Box header scale
    const colors = getGraphicalTakeOffColorPalette(getGraphicalTakeOffColor(this.top));
    const whiteColor = '#fff';
    const point = toPaperPoint(this.dataBox.point);

    const databoxTitle = new PointText({
      point,
      content: this.dataBox.label,
      fontWeight: 'bold',
      justification: 'center',
      fillColor: invertHexColor(colors.primary),
    });

    const titleWidth = databoxTitle.bounds.width;
    width = titleWidth > width ? titleWidth : width;

    const databoxContent = new PointText({
      point,
      content: this.content,
      fontWeight: 'bold',
      justification: 'center',
    });

    const contentWidth = databoxContent.bounds.width;
    width = contentWidth > width ? contentWidth : width;
    width += padding;

    const mainDatabox = new Path.Rectangle(
      new Rectangle(point, new Size(width, height)),
    );
    mainDatabox.position = point;
    mainDatabox.strokeWidth = GTO_MEASUREMENT_STROKE_WIDTH * 2; // tslint:disable-line:no-magic-numbers
    mainDatabox.fillColor = whiteColor;
    mainDatabox.strokeColor = colors.primary;

    const databoxHead = new Path.Rectangle(
      mainDatabox.bounds.topLeft,
      new Size(width, height / headScale),
    );
    databoxHead.fillColor = colors.background;
    databoxHead.strokeColor = colors.background;

    databoxTitle.bounds.center = databoxHead.bounds.center;
    databoxTitle.translate(new Point(0, databoxHead.bounds.center.y - databoxTitle.position.y));
    databoxTitle.insertAbove(databoxHead);

    const databoxBody = new Path.Rectangle(
      databoxHead.bounds.bottomLeft,
      new Size(width, height - databoxHead.bounds.height),
    );
    databoxBody.fillColor = whiteColor;
    databoxBody.strokeColor = colors.background;

    databoxContent.bounds.center = databoxBody.bounds.center;
    databoxContent.translate(new Point(0, databoxBody.bounds.center.y - databoxContent.position.y));
    databoxContent.insertAbove(databoxBody);

    const selectionDatabox = new Path.Rectangle(
      new Rectangle(point, new Size(width, height)),
    );
    selectionDatabox.position = point;
    selectionDatabox.fillColor = 'rgba(0, 0, 0, 0.05)';
    // selectionDatabox.strokeColor = 'blue';
    // selectionDatabox.dashArray = [5, 5];
    selectionDatabox.visible = false;

    // const param = top.takeOffParam;
    // const id = param ? param.takeOffParamId : top.takeOffParamId;
    // const segmentId = this.getSegmentIndex();

    const group = new Group({
      children: [
        mainDatabox,
        databoxHead,
        databoxTitle,
        databoxBody,
        databoxContent,
        selectionDatabox,
      ],
      data: {
        topId: getGraphicalTakeOffTopViewId(this.top),
        measurementId: getGraphicalTakeOffMeasurementId(this.measurement),
        dataBoxId: getGraphicalTakeOffBoxId(this.dataBox),
        type: 'databox',
      },
    });

    moveOnBack(group, 'databox');

    return group;
  }
}
